import { safeLoader } from "@/lib/safe-loader";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { FaqBlock } from "@/components/site/FaqBlock";
import { faqFor } from "@/content/faq-blocks";
import ogHeroImage from "../../assets/preschool-play.webp";
import { createFileRoute } from "@tanstack/react-router";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import preschoolReading from "../../assets/preschool-play.webp";
import preschoolNumbers from "../../assets/preschool-numbers.webp";

export const Route = createFileRoute("/en/preschool")({
  loader: async ({ context }) => {
    const data = await safeLoader("en.preschool.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined);
    return { seo: resolveSeo(data?.content, "forskola", "en") };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absoluteUrl("/en/preschool") },
        { name: "twitter:card", content: "summary_large_image" },
        ...ogImage("/og/forskola-v1.jpg"),
        { property: "og:locale", content: "en_GB" },
      ],
      links: seoLinks("/en/preschool"),
    };
  },
  component: Preschool,
});

function Preschool() {
  const { text } = useContent("forskola");

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      <Prose>
        <img
          src={preschoolReading}
          alt="An educator reading a book together with children at preschool"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={1264}
          height={848}
        />

        <p>
          In Spain, children start practising letters, reading, writing and maths earlier. We follow
          the Swedish preschool curriculum but also want to ensure children have the opportunity for
          this early learning in a pedagogical and playful way at our preschool. At Swedish
          International School, children also become familiar with and practise both Spanish and
          English already at preschool, as we work naturally with both languages in addition to
          Swedish. The school can offer two preschool groups – one Swedish and one international –
          and is only 5–10 minutes from Marbella.
        </p>
        <p>
          Between 9am and 3pm, children from age 3 are welcomed to a fun and educational day in a
          safe environment.
        </p>
        <p>
          The preschool admits children from age 3. If your child has not yet turned 3, you are
          still welcome to contact us: as an exception, and only after an individual assessment
          together with the family, we may be able to offer a place from the age of 2. This is not
          something we can promise in advance.
        </p>

        <h2>Play and social development at the centre</h2>
        <p>
          At the Swedish preschool at Swedish International School, play and social development are
          at the centre. The educators focus on giving children the opportunity to explore and
          discover their surroundings through play and various activities. Children also get the
          chance to develop their language, motor and cognitive skills.
        </p>

        <img
          src={preschoolNumbers}
          alt="Children working with numbers and materials at preschool"
          loading="lazy"
          className="rounded-xl shadow-soft"
          width={810}
          height={540}
        />

        <h2>Continuity in education</h2>
        <p>
          One advantage of attending a Swedish preschool at Swedish International School is that
          children get continuity in their education. The preschool uses Swedish as its main
          language and follows the Swedish curriculum, giving pupils a stable foundation for their
          continued education. Children get an early introduction to the Swedish school system and
          can more easily adapt when they return to Sweden or continue their education at other
          international schools.
        </p>

        <h2>Swedish traditions and culture</h2>
        <p>
          The educators use songs, dances and crafts to introduce children to Swedish traditions and
          holidays. This gives children the opportunity to learn about their heritage and feel part
          of Swedish traditions even while living in Spain.
        </p>
        <p>
          For parents seeking a Swedish-language education, the preschool is a great option. The
          preschool has Swedish-speaking staff and uses Swedish as its main language, giving children
          the chance to maintain and develop their Swedish language skills while having a unique
          experience of growing up in another culture.
        </p>

        <h2>The day at preschool</h2>
        <p>
          The day has a clear rhythm with circle time, activities in small groups, outdoor play, lunch
          and rest. Children spend much of the day outdoors when the weather allows, and indoors there
          is room for crafts, construction play, story time and quiet moments. Preschool is open
          09:00–15:00 on school days and follows the school's{" "}
          <a className="text-primary link-quiet" href="/en/school-year-dates">school year dates</a>.
        </p>

        <h2>Languages in everyday life</h2>
        <p>
          Swedish is the base of the setting, while Spanish and English appear naturally in songs,
          games and conversation during the day. For children who speak Swedish as a second language at
          home, this means plenty of everyday language with Swedish-speaking adults — and for children
          staying on in Spain, an early and secure introduction to Spanish.
        </p>

        <h2>Practical matters before starting</h2>
        <ul>
          <li>Settling-in is planned together with you, based on your child's needs.</li>
          <li>Bring a change of clothes, sun protection, a cap and a labelled water bottle.</li>
          <li>Lunch and snacks are served at school; special diets are stated in the application.</li>
          <li>Report sick leave to the preschool in the morning.</li>
        </ul>

        <h2>Places and fees</h2>
        <p>
          Places are allocated on a rolling basis and numbers are limited, so get in touch in good time
          before your preferred term. Preschool fees are listed on the{" "}
          <a className="text-primary link-quiet" href="/en/fees">fees page</a> and you apply via
          the{" "}
          <a className="text-primary link-quiet" href="/en/application">application form</a>. You
          are welcome to contact us with questions about age, groups or settling in.
        </p>


      </Prose>
      <FaqBlock items={faqFor("forskola", "en")} locale="en" />


      <CtaBand />
      <div className="h-20" />
    </>
  );
}
